import { ITask, Manager, ConversationState, TaskHelper } from "@twilio/flex-ui";
import uhaulStore from "../../../../CustomRedux/UhaulStore";
import GetLiveChatHeader from "../../../../LiveChatHeaderHelper";
import { EncodedParams } from "../../../types/serverless";
import ApiService from "../../../utils/ApiService";
import TaskService from "../../../utils/TaskRouterService";

interface RemoveParticipantRESTPayload {
  conversationSid: string; // CHxxx sid for checking conversation participants
  flexInteractionSid: string; // KDxxx sid for inteactions API
  flexInteractionChannelSid: string; // UOxxx sid for interactions API
  flexInteractionParticipantSid: string; // UTxxx sid for interactions API for the transferrring agent to remove
}

interface TransferRESTPayload {
  taskSid: string; // sid of task to be transferred
  conversationSid: string; // CHxxx sid for linking transfer task in insights
  jsonAttributes: string; // string representation of attributes for new task
  transferTargetSid: string; // worker or queue sid
  transferQueueName: string; // only valid if transfer to queue
  workersToIgnore: object; // {key: value} - where key is the taskrouter attribute to set and value is a string array of names of agents in conversation to make sure they don't get reservations to join again
  flexInteractionSid: string; // KDxxx sid for inteactions API
  flexInteractionChannelSid: string; // UOxxx sid for interactions API
  removeFlexInteractionParticipantSid: string; // UTxxx sid for interactions API for the transferrring agent to remove them from conversation
}

interface EndChatRESTPayload {
  conversationSid: string; // CHxxx sid for checking conversation participants
}

const _getMyParticipantSid = (participants: any): string => {
  const manager = Manager.getInstance();
  const myParticipant = participants.find(
    (participant: any) => participant.mediaProperties?.identity === manager.conversationsClient?.user?.identity
  );

  return myParticipant ? myParticipant.participantSid : "";
};

const _getAgentsWorkerSidArray = (participants: any) => {
  return participants.reduce((prevArray: string[], currentParticipant: any) => {
    if (currentParticipant.type === "agent" && currentParticipant?.routingProperties?.workerSid) {
      return [...prevArray, currentParticipant?.routingProperties?.workerSid];
    }
    return prevArray;
  }, []);
};

const _queueNameFromSid = async (transferTargetSid: string) => {
  const manager = Manager.getInstance();
  const queues = await new TaskService(manager).getQueues();
  const queueResult = queues
    ? queues.find((queue) => {
        return queue.sid === transferTargetSid;
      })
    : null;

  return queueResult?.friendlyName || "";
};

export const buildRemoveMyPartiticipantAPIPayload = async (
  conversation: ConversationState.ConversationState
): Promise<RemoveParticipantRESTPayload | null> => {
  const conversationSid = conversation.source?.sid;
  if (conversationSid === undefined) return null;
  const task = TaskHelper.getTaskFromConversationSid(conversationSid);
  if (!task || !TaskHelper.isCBMTask(task)) return null;

  const { flexInteractionSid = "", flexInteractionChannelSid = "" } = task.attributes;

  const participants = await task.getParticipants(flexInteractionChannelSid);

  const flexInteractionParticipantSid = _getMyParticipantSid(participants);

  if (!flexInteractionParticipantSid) return null;

  return {
    conversationSid,
    flexInteractionSid,
    flexInteractionChannelSid,
    flexInteractionParticipantSid
  };
};

export const buildRemovePartiticipantAPIPayload = (
  conversationSid: string,
  task: ITask,
  flexInteractionParticipantSid: string
): RemoveParticipantRESTPayload | null => {
  if (!task || !TaskHelper.isCBMTask(task)) return null;
  if (!flexInteractionParticipantSid) return null;

  const { flexInteractionSid = "", flexInteractionChannelSid = "" } = task.attributes;

  return {
    conversationSid,
    flexInteractionSid,
    flexInteractionChannelSid,
    flexInteractionParticipantSid
  };
};

export const buildInviteParticipantAPIPayload = async (
  task: ITask,
  targetSid: string,
  removeInvitingAgent: boolean
): Promise<TransferRESTPayload | null> => {
  const { taskSid } = task;
  const conversationSid = task.attributes?.conversations?.conversation_id || task.taskSid;
  const jsonAttributes = JSON.stringify(task.attributes);
  const transferTargetSid = targetSid;

  let transferQueueName = "";
  if (transferTargetSid.startsWith("WQ")) {
    transferQueueName = await _queueNameFromSid(transferTargetSid);
    if (!transferQueueName) {
      console.error("Transfer failed. queueNameFromSid failed for", transferTargetSid);
      return null;
    }
  }

  const { flexInteractionSid = null, flexInteractionChannelSid = null } = task.attributes;

  if (!flexInteractionSid || !flexInteractionChannelSid) {
    console.error("Transfer failed. Missing flexInteractionSid or flexInteractionChannelSid", task.sid);
    return null;
  }

  const participants = await task.getParticipants(flexInteractionChannelSid);

  const workerSidsInConversationArray = _getAgentsWorkerSidArray(participants);
  const workersToIgnore = {
    workerSidsInConversation: workerSidsInConversationArray
  };

  let removeFlexInteractionParticipantSid = "";
  if (removeInvitingAgent) {
    removeFlexInteractionParticipantSid = _getMyParticipantSid(participants) || "";

    if (!removeFlexInteractionParticipantSid) {
      console.error("Transfer failed. Didn't find flexInteractionPartipantSid", task.sid);
      return null;
    }
  }

  return {
    taskSid,
    conversationSid,
    jsonAttributes,
    transferTargetSid,
    transferQueueName,
    workersToIgnore,
    flexInteractionSid,
    flexInteractionChannelSid,
    removeFlexInteractionParticipantSid
  };
};

interface TransferRESTResponse {
  success: boolean;
  invitesTaskSid: string;
}

interface RemoveParticipantRESTResponse {
  success: boolean;
}

interface EndChatRESTResponse {
  success: boolean;
}

function returnResult(response: TransferRESTResponse): TransferRESTResponse {
  return {
    ...response
  };
}

export default class ChatTransferService extends ApiService {
  sendTransferChatAPIRequest = async (requestPayload: TransferRESTPayload): Promise<TransferRESTResponse> => {
    return this.fetchJsonWithReject<TransferRESTResponse>(
      `${
        uhaulStore.getState().UhaulReducer.config.liveChatApiUrl
      }invite-participant`,
      {
        method: "POST",
        headers: GetLiveChatHeader(),
        body: JSON.stringify({
            taskSid: requestPayload.taskSid,
            conversationSid: requestPayload.conversationSid,
            jsonAttributes: requestPayload.jsonAttributes,
            transferTargetSid: requestPayload.transferTargetSid,
            transferQueueName: requestPayload.transferQueueName,
            workersToIgnore: JSON.stringify(requestPayload.workersToIgnore),
            flexInteractionSid: requestPayload.flexInteractionSid,
            flexInteractionChannelSid: requestPayload.flexInteractionChannelSid
          })
      }
    ).then(returnResult);
  };

  removeParticipantAPIRequest = async (
    requestPayload: RemoveParticipantRESTPayload
  ): Promise<RemoveParticipantRESTResponse> => {

    return this.fetchJsonWithReject<TransferRESTResponse>(
      `${
        uhaulStore.getState().UhaulReducer.config.liveChatApiUrl
      }Participant`,
      {
        method: "delete",
        headers: GetLiveChatHeader(),
        body: JSON.stringify({ 
          ConversationSid: requestPayload.conversationSid,
          FlexInteractionSid: requestPayload.flexInteractionSid,
          FlexInteractionChannelSid: requestPayload.flexInteractionChannelSid,
          FlexInteractionParticipantSid: requestPayload.flexInteractionParticipantSid
        })
      }
    ).then(returnResult);
  };

  endAllChatsRequest = async (
    requestPayload: EndChatRESTPayload
  ): Promise<EndChatRESTResponse> => {
    const encodedParams: EncodedParams = {
      Token: encodeURIComponent(this.manager.user.token),
      conversationSid: encodeURIComponent(requestPayload.conversationSid)
    };

    return this.fetchJsonWithReject<TransferRESTResponse>(
      `${
        uhaulStore.getState().UhaulReducer.currentUser.featuresBaseUrl
      }/features/conversation-transfer/flex/end-chat`,
      {
        method: "post",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: this.buildBody(encodedParams)
      }
    ).then(returnResult);
  };
}